import React, { Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { PageProvider } from "./context/PageContext";
import { IntroProvider } from "./context/IntroContext";
import Preloader from "./components/layout/Preloader";
import SmoothScroll from "./components/layout/SmoothScroll";
import RouteTransition from "./components/layout/RouteTransition";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import Cursor from "./components/layout/Cursor";
import { services } from "./data/site";
import { legalPages } from "./data/legal";
import Home from "./pages/Home";

// Code-split routes; preloaded when the browser is idle so navigation never waits
const lazyPage = factory => {
  const Component = React.lazy(factory);
  Component.preload = factory;
  return Component;
};
const ServicePage = lazyPage(() => import("./pages/ServicePage"));
const AboutPage = lazyPage(() => import("./pages/AboutPage"));
const ContactPage = lazyPage(() => import("./pages/ContactPage"));
const BlogPage = lazyPage(() => import("./pages/BlogPage"));
const BlogPostPage = lazyPage(() => import("./pages/BlogPostPage"));
const LegalPage = lazyPage(() => import("./pages/LegalPage"));
const NotFound = lazyPage(() => import("./pages/NotFound"));
const lazyPages = [ServicePage, AboutPage, ContactPage, BlogPage, BlogPostPage, LegalPage, NotFound];
// /lab was the WebGL prototype's address; it is now the landing page. Old links keep their
// ?world= / ?quality= and land on / before the router starts (no curtain on arrival).
if (window.location.pathname === "/lab") window.history.replaceState(null, "", `/${window.location.search}`);

// Label shown on the route-transition curtain
const titleFor = pathname => {
  if (pathname === "/") return "Home";
  const service = services.find(s => s.path === pathname);
  if (service) return service.label;
  const legal = legalPages.find(p => p.path === pathname);
  if (legal) return legal.label;
  if (pathname.startsWith("/blog/")) return "Insights / Article";
  return { "/about": "About", "/contact": "Contact", "/blog": "Insights" }[pathname] || "Dreamvision";
};

const AppToaster = () => {
  const { theme } = useTheme();
  return (
    <Toaster
      theme={theme}
      position="bottom-center"
      toastOptions={{
        classNames: {
          toast: "!rounded-2xl !border !border-border !bg-card !text-foreground !shadow-2xl",
          description: "!text-muted-foreground"
        }
      }}
    />
  );
};

function App() {
  useEffect(() => {
    const idle = window.requestIdleCallback || (cb => setTimeout(cb, 1500));
    const handle = idle(() => lazyPages.forEach(page => page.preload()));
    return () => (window.cancelIdleCallback || clearTimeout)(handle);
  }, []);

  return (
    <ThemeProvider>
      <IntroProvider>
        <BrowserRouter>
          <PageProvider>
            <SmoothScroll>
              <a
                href="#main"
                className="fixed left-4 top-4 z-[100] -translate-y-24 rounded-full bg-foreground px-4 py-2 text-sm text-background transition-transform focus:translate-y-0"
              >
                Skip to content
              </a>
              <Header />
              <main id="main">
                <RouteTransition titleFor={titleFor}>
                  {location => (
                    <Suspense fallback={<div className="min-h-screen" />}>
                      <Routes location={location}>
                        <Route path="/" element={<Home />} />
                        {services.map(service => (
                          <Route key={service.slug} path={service.path} element={<ServicePage slug={service.slug} />} />
                        ))}
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/blog" element={<BlogPage />} />
                        <Route path="/blog/:categoryId/:postSlug" element={<BlogPostPage />} />
                        {legalPages.map(page => (
                          <Route key={page.slug} path={page.path} element={<LegalPage slug={page.slug} />} />
                        ))}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  )}
                </RouteTransition>
              </main>
              <Footer />
              <Cursor />
              <Preloader />
              <div className="grain" aria-hidden="true" />
              <AppToaster />
            </SmoothScroll>
          </PageProvider>
        </BrowserRouter>
      </IntroProvider>
    </ThemeProvider>
  );
}

export default App;
